import { Component, Input, OnInit, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { API, MarkerFullDTO } from '../../services/api/api';
import { ModalNavigationService } from '../../services/gui/modal/modal-navigation.service';
import { UtilService } from '../../services/util.service';
import { ErrorService } from '../../services/error.service';
import { SbSplitButton } from '../../services/global/global-interfaces';
import { BookingEditorService } from '../../services/booking-editor/booking-editor.service';
import { iif } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalNavigationComponent } from '../../services/gui/modal/modal-navigation-component';
import { RequestClass } from '../../services/global/RequestClass';

type SplitButtonAction = 'select';

@Component({
  selector: 'sb-comp-marker-list',
  templateUrl: './sb-marker-list.component.html',
  styleUrls: ['./sb-marker-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbMarkerListComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly util = inject(UtilService);
  public readonly bookingEditorService = inject(BookingEditorService);
  private readonly api = inject(API);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly errorService = inject(ErrorService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() requirementId: number;

  // global variables
  isInModalContext: boolean;
  planningId = null;

  // table wrapper variables
  markers: MarkerFullDTO[];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit(): void {
    if (this.isInModalContext) {
      this.filterConfig = { ...this.filterConfig, ignoreQueryParams: true };
      this.requirementId = this.modalNavigation.getCurrentNavigation().data.requirementId;
      this.planningId = this.bookingEditorService.planningId;
    }
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = iif(
      () => this.planningId,
      this.api.ds_cm_Requirement_retrieveNonBookedMarkersPlanning(
        this.requirementId,
        this.planningId,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      ),
      this.api.ds_cm_Requirement_retrieveNonBookedMarkers(
        this.requirementId,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
    )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (markers: MarkerFullDTO[]) => {
          this.markers = this.buildSplitButtons(markers);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  selectMarker(selectedMarker: MarkerFullDTO) {
    console.log('selectemarker', selectedMarker);
    this.modalNavigation.result(selectedMarker);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'boxChecked',
        action: 'select',
        tooltip: 'lang.globals.select',
      });
    });
    return result;
  }

  resolveSplitButtonAction(marker: MarkerFullDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'select':
        this.selectMarker(marker);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
